import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { builds, keyboards, switches, keycaps, plates } from './dataStore';
import galleryImages, { featuredBuildByKeyboard, galleryList } from './galleryData';
import { AudioPlayer } from './components/AudioProvider';
import { Button } from './components/Button';


function GalleryPreviewDrawer({ open, kb, onClose, onExited }) {
	const [shouldRender, setShouldRender] = useState(open);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		if (open) {
			setShouldRender(true);
			// Start hidden, then trigger visible after a tick for transition
			const timeout = setTimeout(() => setVisible(true), 10);
			return () => clearTimeout(timeout);
		} else {
			setVisible(false);
			const timeout = setTimeout(() => {
				setShouldRender(false);
				if (onExited) onExited();
			}, 300);
			return () => clearTimeout(timeout);
		}
	}, [open, onExited]);

	if (!shouldRender || !kb) return null;
	const keyboard = keyboards[kb.keyboardId];
	const sw = switches[kb.switchId];
	const kc = keycaps[kb.keycapId];
    const pl = plates[kb.plateId];
	const soundtests = [kb.audio] || [];
	return (
		<div
			className={`fixed inset-0 z-[9999] flex items-center justify-end transition-opacity duration-300 backdrop-blur-sm ${visible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
			onClick={onClose}
		>
			<div
				className={`relative w-full max-w-md h-full bg-[#1e1e21] backdrop-blur-lg shadow-2xl flex flex-col gap-6 p-0 transform transition-transform duration-300 ${visible ? 'translate-x-0' : 'translate-x-full'}`}
				onClick={(e) => e.stopPropagation()}
			>

				<button
					className='absolute top-3 left-3 z-10 p-1 bg-transparent border-none opacity-70 hover:opacity-100'
					onClick={onClose}
					style={{ lineHeight: 0 }}
				>
					<svg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg' className='pointer-events-none text-white drop-shadow-md'>
						<line x1='8' y1='8' x2='20' y2='20' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
						<line x1='20' y1='8' x2='8' y2='20' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
					</svg>
				</button>


				<img
					src={galleryImages[kb.keyboardId] || keyboard?.image || '/pictures/home_bg.JPG'}
					alt={keyboard?.name || 'Keyboard'}
					className='w-full h-64 object-cover m-0 rounded-none'
				/>


				<div className='p-8 flex flex-col gap-4'>
					<h2 className='text-2xl font-bold text-gray-300'>{kb.name}</h2>
                    <p className='text-gray-300 text-base'>
                        <span className='font-semibold'>Color:</span> {keyboard.color || '-'}<br/>
                        <span className='font-semibold'>Keycaps:</span> {kc?.name || '-'}<br/>
                        <span className='font-semibold'>Switches:</span> {sw?.name || '-'}<br/>
                        <span className='font-semibold'>Plate:</span> {pl?.material || '-'}<br/>
                        <span className='font-semibold'>Mount:</span> {kb.mount || '-'}<br/>
                        <span className='font-semibold'>Foam:</span> {kb.foam && kb.foam.length > 0 ? kb.foam.join(' + ') : 'No foam'}
					</p>
					{soundtests.length > 0 && (
						<div className='flex flex-col gap-2 mt-2'>
							{soundtests.map((url, idx) => (
								<AudioPlayer src={url} key={idx} className='w-full' />
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

// Show one card per keyboard, using the featured build for that keyboard
const keyboardList = galleryList
	.map((id) => keyboards[id]);

function GalleryPage() {
	const [preview, setPreview] = useState(null);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [visibleCount, setVisibleCount] = useState(6);

	const handleCardClick = (keyboard, build) => {
		setPreview({ keyboard, build });
		setDrawerOpen(true);
	};
	const handleClose = () => setDrawerOpen(false);
	const handleDrawerExited = () => setPreview(null);

	const visibleKeyboards = keyboardList.slice(0, visibleCount);
	const hasMore = visibleCount < keyboardList.length;

	return (
		<div className='min-h-screen bg-[#111112] py-24 px-4 relative overflow-hidden'>
			{/* Ambient blurred backdrop */}
			<div
				aria-hidden='true'
				className='pointer-events-none select-none fixed inset-0 w-full h-full z-0'
				style={{
					backgroundImage: "url('/pictures/gallery/gallery_bg.JPG')",
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					filter: 'blur(12px) brightness(0.5)',
					WebkitFilter: 'blur(12px) brightness(0.5)',
					zIndex: 0,
				}}
			/>
			{/* Main content */}
			<div className='relative z-10'>
				<h1 className='text-3xl font-bold text-white mb-6 text-center drop-shadow'>
					Keyboard Gallery
				</h1>
				<p className='text-gray-300 mb-16 text-center text-base mr-[12vw] ml-[12vw]'>
					This collection contains all the keyboards featured in the AEYKBDS YouTube channel. Click on the photo for a quick preview of our favorite build or click the
					<span className='text-blue-400 font-semibold'> Details →</span> link to see the full review, sound test, and more photos.
				</p>
				<div className='grid gap-8 md:grid-cols-3'>
					{visibleKeyboards.map((keyboard) => {
						const buildId = featuredBuildByKeyboard[keyboard.id];
						const build = builds[buildId];
						const sw = build ? switches[build.switchId] : null;
						const kc = build ? keycaps[build.keycapId] : null;
						const pl = build ? plates[build.plateId] : null;
						return (
							<div
								key={keyboard.id}
								className='backdrop-blur-lg bg-black/80 border border-gray-900 shadow-xl hover:scale-105 transition transform cursor-pointer rounded'
								onMouseUp={(e) => {
									if(e.target.closest('a')) return;
									handleCardClick(keyboard, build);
								}}
							>
								<img
									src={galleryImages[keyboard.id] || keyboard.image || '/pictures/home_bg.JPG'}
									alt={keyboard.name}
									className='w-full h-48 object-cover rounded-t'
								/>
								<div className='p-6'>
									<h2 className='text-lg font-bold text-gray-300 mb-2'>
										{keyboard.name}
                                        {', ' + keyboard.color}
									</h2>
									{build && (
										<p className='text-gray-300 mb-2'>
											{pl ? `Plate: ${pl.material}` : ''}
											{sw ? `, Switches: ${sw.name}` : ''}
											{kc ? `, Keycaps: ${kc.name}` : ''}
											{build.foam && build.foam.length > 0 ? `, Foam: ${build.foam.join(' + ')}` : ', No foam'}
											{build.mount ? `, Mount: ${build.mount}` : ''}
										</p>
									)}
									<Link 
										to={`/articles/${keyboard.articleid}`}
										className='relative text-blue-400 after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-[2px] after:bg-blue-400 after:transition-all after:duration-300 hover:after:w-full transition px-0' 
										onClick={(e) => e.stopPropagation()}
									>
										<span className='relative z-10 font-semibold'>Details →</span>
									</Link>
								</div>
							</div>
						);
					})}
				</div>
				{hasMore && (
					<div className='flex justify-center mt-8'>
						<Button onClick={() => setVisibleCount((c) => c + 6)} children="Load More" className="text-white font-semibold px-6 py-2" />
					</div>
				)}
			</div>
			{/* Preview Drawer rendered at root level to avoid stacking context issues */}
			{preview && preview.build && (
				<GalleryPreviewDrawer
					open={drawerOpen}
					kb={{...preview.build, keyboardId: preview.keyboard.id}}
					onClose={handleClose}
					onExited={handleDrawerExited}
				/>
			)}
		</div>
	);
}

export default GalleryPage;
